import { PackageCheck, RefreshCw, CornerDownLeft } from 'lucide-react';
import { OrderStatusTab } from '@/types/columns';
import { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import { STATUS_HSL } from '@/pages/order-details/components/StatusColors';
import { ON_HOLD_FILTER_OPTIONS, StatusFilterOption } from '../ordersFilterOptions';

// Status sub-options per bucket. key = API sub-tab value (matches listStatusButtons.value).
export const TAB_STATUS_OPTIONS: Record<string, StatusFilterOption[]> = {
  [OrderStatusTab.READY_TO_SHIP]: [
    { key: 'new', badgeColorKey: 'new', labelKey: 'new' },
    { key: 'scheduled', badgeColorKey: 'scheduled', labelKey: 'scheduled' },
  ],
  [OrderStatusTab.IN_PROGRESS]: [
    { key: 'collecting', badgeColorKey: 'collecting', labelKey: 'collecting' },
    { key: 'collected', badgeColorKey: 'collected', labelKey: 'collected' },
    { key: 'en-route', badgeColorKey: 'in_transit', labelKey: 'in_transit' },
    { key: 'out-for-delivery', badgeColorKey: 'out_for_delivery', labelKey: 'out_for_delivery' },
    { key: 'postponed', badgeColorKey: 'postponed', labelKey: 'postponed' },
    // Hold-resolved orders are listed under In progress (core status_group), so is their filter (owner 11-03).
    { key: 'hold-resolved', badgeColorKey: 'hold_resolved', labelKey: 'hold_resolved' },
  ],
  [OrderStatusTab.ON_HOLD]: ON_HOLD_FILTER_OPTIONS,
  [OrderStatusTab.RTO]: [
    { key: 'rto-requested', badgeColorKey: 'rto_requested', labelKey: 'rto_request' },
    { key: 'rto-scheduled', badgeColorKey: 'rto_scheduled', labelKey: 'return_scheduled' },
    {
      key: 'rto-received-warehouse',
      badgeColorKey: 'rto_received_warehouse',
      labelKey: 'returned_to_warehouse',
    },
    { key: 'rto-in-transit', badgeColorKey: 'rto_in_transit', labelKey: 'rto_in_transit' },
  ],
  [OrderStatusTab.FINISHED]: [
    { key: 'delivered', badgeColorKey: 'delivered', labelKey: 'delivered' },
    { key: 'rto-delivered', badgeColorKey: 'rto_delivered', labelKey: 'returned' },
    {
      key: 'rto-partially-delivered',
      badgeColorKey: 'rto_partially_delivered',
      labelKey: 'part_returned',
    },
    {
      key: 'partially-delivered',
      badgeColorKey: 'partially_delivered',
      labelKey: 'partially_delivered',
    },
    { key: 'claim-settled', badgeColorKey: 'claim_settled', labelKey: 'claim_settled' },
    { key: 'resend', badgeColorKey: 'resend', labelKey: 'resend' },
  ],
};

/** Status badge colours: options use underscores, the central map hyphens. */
export const getFilterBadgeColor = (badgeKey: string) =>
  STATUS_HSL[badgeKey.replace(/_/g, '-')] ??
  STATUS_HSL[badgeKey] ?? { bg: 'hsla(0,0%,50%,0.10)', text: 'hsl(0,0%,40%)' };

export const ORDER_TYPES = [
  { value: OrderTypeEnum.NORMAL, labelKey: 'order_type_normal', Icon: PackageCheck },
  { value: OrderTypeEnum.RETURN, labelKey: 'order_type_returned', Icon: CornerDownLeft },
  { value: OrderTypeEnum.EXCHANGE, labelKey: 'order_type_exchange', Icon: RefreshCw },
];

export const PRINTED_OPTIONS = [
  { value: true, labelKey: 'printed' },
  { value: false, labelKey: 'not_printed' },
];

/** Bucket key → i18n label (the bucket names merchants know). */
export const TAB_LABEL_KEYS: Record<string, string> = {
  ready_to_ship: 'with_you',
  on_hold: 'problematic',
  return_to_origin: 'return_to_origin',
  finished: 'completed',
};

export const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sd-foreground';

export const formatCount = (n: number) => new Intl.NumberFormat('en-US').format(n);
